import { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import BillComponent from "./BillComponent";
import useDetectPrint from "../../hooks/useDetectPrint";
import { isMealBillAndroid, androidPrinter } from "../../native/mealbill-android.mjs";
import moment from "moment";

/**
 * Build a flat ESC/POS receipt matching the KOTcomponent/BillComponent.js layout:
 * Header (restaurant, address, GST) → customer name → date + token → type + table
 * → Sr / Product / Qty table → Total → Remarks
 *
 * jobId format: "kot:<branchOrderNumber>:<kotIndex>" — stable per print event.
 */
function createAndroidKOTReceipt({ printData, restaurant, branchAddress, gstNumber, W }) {
  const lastKot = printData?.KOTS?.[printData.KOTS.length - 1];
  const kotIndex = printData?.KOTS?.length ?? 1;
  const branchOrderNumber = (printData?.branchOrderNumber ?? "").replace(/\s/g, "");
  const jobId = `kot:${branchOrderNumber}:${kotIndex}`.replace(/[^A-Za-z0-9._:-]/g, "-").slice(0, 128);

  const SEP = "-".repeat(W);
  const spreadEven = (...parts) => {
    const p = parts.filter(Boolean);
    if (!p.length) return "";
    if (p.length === 1) return p[0];
    const tl = p.reduce((s, x) => s + x.length, 0);
    const sp = W - tl;
    const gaps = p.length - 1;
    const spc = sp > 0 ? Math.floor(sp / gaps) : 1;
    const extra = sp > 0 ? sp - spc * gaps : 0;
    return p.map((x, i) => i < p.length - 1 ? x + " ".repeat(spc + (i < extra ? 1 : 0)) : x).join("");
  };

  const lines = [];

  // Header: Restaurant name + address, NO separator line after
  if (restaurant) lines.push({ text: restaurant, align: "center", bold: true });
  if (branchAddress) lines.push({ text: branchAddress, align: "center" });
  if (gstNumber) lines.push({ text: `GST ${gstNumber}`, align: "center" });

  // ── Info rows (no surrounding separators) ─────────────────────────────────
  const { customerName, customerMobile, orderType, tableNumber, remarks } = printData;
  const kotNumber = lastKot?.kotNumber;

  // Customer name row (only if present)
  if (customerName) {
    lines.push({ text: `Name : ${customerName} ${customerMobile ? `(${customerMobile})` : ""}` });
  }

  // Row: date (left)  |  Token : N (right)
  lines.push({ columns: [moment().format("DD/MM/YYYY HH:mm"), kotNumber ? `Token : ${kotNumber}` : ""] });

  // Row: orderType (left)  |  Table : N (right)
  lines.push({ columns: [orderType ?? "", tableNumber ? `Table : ${tableNumber}` : ""] });

  // Separator BEFORE the item table
  lines.push({ text: SEP, align: "center" });

  // Item table header: #  Item | Qty
  lines.push({ columns: ["#  Item", "Qty"], bold: true });
  lines.push({ text: SEP, align: "center" });

  // ── Items ─────────────────────────────────────────────────────────────────
  let totalQty = 0;
  let sr = 0;
  (lastKot?.orderItems ?? []).forEach((item) => {
    const qty = item.quantity ?? 0;
    totalQty += qty;
    sr++;
    lines.push({ columns: [`${sr}  ${item.itemName ?? ""}`, String(qty)] });
    // Toppings row
    if (item?.activeTopping?.length > 0) {
      const toppingNames = item.activeTopping.map((t) => t.itemName).join(",");
      lines.push({ columns: ["-  " + toppingNames, ""] });
    }
  });

  // Separator after items
  lines.push({ text: SEP, align: "center" });

  // Total right-aligned
  lines.push({ columns: ["", `Total  ${totalQty}`], bold: true });

  // ── Remarks ───────────────────────────────────────────────────────────────
  if (remarks) {
    lines.push({ text: "" });
    lines.push({ text: `Remarks : ${remarks}`, size: 26 });
  }

  return { jobId, printer: "kitchen", lines };
}

const PrintComponent = ({
  headers,
  footers,
  printData,
  restaurant,
  logo,
  gstNumber,
  containerStyle,
  receiptMessage,
  branchAddress,
  printSetting,
}) => {
  const isPrinting = useDetectPrint();

  const componentRef = useRef();
  const lastPrintData = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // alert("print completed");
    },
  });

  useEffect(() => {
    if (isPrinting || !printData) return;

    // ── Android native thermal print path ──────────────────────────────────
    if (isMealBillAndroid()) {
      try {
        if (lastPrintData.current === printData) return; // Deduplicate Strict Mode double renders
        lastPrintData.current = printData;

        androidPrinter.getProfiles().then(result => {
          const profile = result.profiles?.kitchen;
          const W = (profile?.width === 576 || profile?.width === 832) ? 42 : 32;
          
          const receipt = createAndroidKOTReceipt({ printData, restaurant, branchAddress, gstNumber, W });
          receipt.jobId = `${receipt.jobId}:${Date.now()}`;

          return androidPrinter.print(receipt);
        }).then(
          (result) => console.log("Android KOT print:", result.status, receipt.jobId),
          (error) => {
            console.error("Android KOT print failed:", error.message, error.result);
            window.alert(`Print failed: ${error.message}`);
          }
        );
      } catch (error) {
        console.error("Android KOT receipt build error:", error.message);
        window.alert(`Print error: ${error.message}`);
      }
      return; // Never fall back to browser print after native attempt
    }

    // ── Electron desktop path ───────────────────────────────────────────────
    console.log("isElectron", window.api);
    if (window?.api?.isElectron) {
      console.log("isElectron inside", window.api);
      window.api.send(printData);
    } else {
      // ── Browser fallback (print dialog) ──────────────────────────────────
      handlePrint();
    }
  }, [printData, isPrinting]);

  return (
    <div style={containerStyle}>
      <div style={{ display: "none" }}>
        <BillComponent
          ref={componentRef}
          orderData={printData}
          restaurant={restaurant}
          logo={logo}
          branchAddress={branchAddress}
        />
      </div>
    </div>
  );
};
export default PrintComponent;
